import { useState, useMemo } from 'react'
import { Box, Button, Link, Paper, Typography } from '@mui/material'
import { useHistory } from 'react-router-dom'
import BasicInput from '../../../../components/BasicComponent/BasicInput'
import BasicSelect from '../../../../components/BasicComponent/BasicSelect'
import { PATH } from '../../../../constants/router'
import { PARAM_IS_EMBEDDED_VIEW, PARAM_IS_HEADER_MINI_APP } from '../../../../constants/params'
import { useAppParamsContext } from '../../../../context/AppParamsContext'
import { normalizePlate, validatePlateNumber } from '../../../../helper/validatorPlateNumber'
import TrafficViolationsServices from '../../../../services/TrafficViolationsServices'
import {
  TRAFFIC_ERROR_MESSAGES,
  TRAFFIC_SEARCH_TYPE,
  TRAFFIC_THEME,
  TRAFFIC_URLS,
  TRAFFIC_VEHICLE_TYPES
} from '../../constants'
import LoadingSpinner from '../LoadingSpinner'
import './index.scss'

const VehicleTypeIconSelector = ({ value, onChange, options, error }) => {
  return (
    <div className="TrafficViolationsViolationLookup_vehicleTypeGroupWrapper">
      <div className="TrafficViolationsViolationLookup_vehicleTypeGroup">
        {options.map((opt) => (
          <div
            key={opt.value}
            className={`TrafficViolationsViolationLookup_vehicleTypeCard ${value?.value === opt.value ? 'active' : ''}`}
            onClick={() => {
              if (onChange) onChange(opt)
            }}>
            <img src={opt.icon} alt={opt.label} />
            <span>{opt.label}</span>
          </div>
        ))}
      </div>
      {error && <div className="TrafficViolationsViolationLookup_vehicleTypeError">{error}</div>}
    </div>
  )
}

const validatePhone = (value, showPhoneInput = true) => {
  if (!showPhoneInput) {
    return ''
  }

  if (!value) {
    return 'Vui lòng nhập số điện thoại.'
  }

  const phone = String(value).trim()

  // if (phone.length < 6) {
  //   return 'Tối thiểu 6 ký tự.'
  // }

  // if (phone.length > 20) {
  //   return 'Tối đa 20 ký tự.'
  // }

  const regex = /^0(32|33|34|35|36|37|38|39|96|97|98|86|81|82|83|84|85|88|91|94|70|76|77|78|79|89|90|93|52|56|58|92|59|99)\d{7}$/
  if (!regex.test(phone)) {
    return 'Số điện thoại không hợp lệ.'
  }

  return ''
}

const digitsOnly = (value) => String(value || '').replace(/[^\d]/g, '')

export default function ViolationLookup() {
  const history = useHistory()
  const {
    isEmbeddedView,
    isHeaderMiniApp,
    showPhoneInput,
    showBannerInEmbeddedView,
    sdkPhoneNumber,
    isSelectVehicleTypeUseIcon,
    isTBPNSelectVehicleTypeWithElectricMotobike
  } = useAppParamsContext()

  const vehicleTypeOptions = useMemo(() => {
    const options = [TRAFFIC_VEHICLE_TYPES.oto, TRAFFIC_VEHICLE_TYPES.xemay]

    if (isTBPNSelectVehicleTypeWithElectricMotobike) {
      options.push(TRAFFIC_VEHICLE_TYPES.maydien)
    }

    return options
  }, [isTBPNSelectVehicleTypeWithElectricMotobike])

  const [searchType] = useState(TRAFFIC_SEARCH_TYPE.csgt)
  const [carType, setCarType] = useState(TRAFFIC_VEHICLE_TYPES.oto)
  const [plateNumber, setPlateNumber] = useState('')
  const [phoneNumber, setPhoneNumber] = useState(() => digitsOnly(sdkPhoneNumber))

  const [errors, setErrors] = useState({})
  const [isLookup, setIsLookup] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAPIError, setIsAPIError] = useState(false)
  const [isVehicleWarning, setIsVehicleWarning] = useState(false)
  const [criminalNotification, setCriminalNotification] = useState(null)
  const [linkSearch, setLinkSearch] = useState(null)

  const appendEmbedParams = (url) => {
    const params = []

    if (isEmbeddedView) {
      params.push(`${PARAM_IS_EMBEDDED_VIEW}=true`)
    }

    if (isHeaderMiniApp) {
      params.push(`${PARAM_IS_HEADER_MINI_APP}=true`)
    }

    if (!params.length) {
      return url
    }

    return url.includes('?') ? `${url}&${params.join('&')}` : `${url}?${params.join('&')}`
  }

  const handleCarTypeChange = (value) => {
    setCarType(value)
    setIsLookup(false)
    setIsAPIError(false)

    if (!value) {
      setErrors((prev) => ({ ...prev, carType: 'Bạn cần chọn loại xe.' }))
      return
    }

    setErrors((prev) => ({ ...prev, carType: '' }))
  }

  const validateForm = () => {
    const formErrors = {}
    let isValid = true

    if (!carType) {
      formErrors.carType = 'Bạn cần chọn loại xe.'
      isValid = false
    }

    const plateError = validatePlateNumber(plateNumber)
    if (plateError) {
      formErrors.plateNumber = plateError
      isValid = false
    }

    if (showPhoneInput) {
      const phoneDigits = digitsOnly(phoneNumber)
      const phoneError = validatePhone(phoneDigits, showPhoneInput)

      if (phoneError) {
        formErrors.phoneNumber = phoneError
        isValid = false
      }
    }

    return { isValid, formErrors }
  }

  const handleSubmit = async () => {
    const { isValid, formErrors } = validateForm()
    setErrors(formErrors)

    if (!isValid) {
      return
    }

    setIsLookup(true)
    setIsLoading(true)
    setIsAPIError(false)

    const phoneDigits = digitsOnly(phoneNumber)

    try {
      const payload = {
        licensePlates: plateNumber,
        vehicleType: carType?.value,
        phoneNumber: showPhoneInput ? phoneDigits : undefined
      }

      const response = await TrafficViolationsServices.CustomerCriminalRecordUserUserCheckCriminalFromVr(payload, false)
      const criminalData = response?.data

      if (criminalData?.hasCriminal === 1) {
        setIsVehicleWarning(true)
        setCriminalNotification(criminalData?.notificationCSGT)
      } else {
        setIsVehicleWarning(false)
        setCriminalNotification(null)
      }

      setLinkSearch(searchType?.link || null)
    } catch (error) {
      const apiError = error?.error || error?.data?.error
      setIsVehicleWarning(false)
      setCriminalNotification(TRAFFIC_ERROR_MESSAGES?.[apiError]?.message || TRAFFIC_ERROR_MESSAGES.DEFAULT.message)
      setIsAPIError(true)
      setLinkSearch(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenNotification = () => {
    if (!TRAFFIC_URLS.trafficFineWeb) {
      return
    }

    const url = `${TRAFFIC_URLS.trafficFineWeb}/automated-traffic-fine-notification/vehicles?isEmbeddedView=true`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleGoToDetail = () => {
    const url = appendEmbedParams(
      `${PATH.TRAFFIC_VIOLATIONS_RESULT}?lisencePlate=${plateNumber}&carType=${carType?.value}&carTypeLabel=${carType?.label}`
    )

    history.push(url)
  }

  const resultBackground = isVehicleWarning
    ? TRAFFIC_THEME.warningBackground
    : isAPIError
    ? TRAFFIC_THEME.disableBackground
    : TRAFFIC_THEME.successBackground

  return (
    <Paper
      elevation={3}
      className="TrafficViolationsViolationLookup"
      sx={{
        p: 3,
        mb: 4,
        borderRadius: 2,
        border: `1px solid ${TRAFFIC_THEME.cardBorder}`
      }}>
      {(!isEmbeddedView || showBannerInEmbeddedView) && (
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: 'var(--v1-brand-primary)',
            fontWeight: 'bold',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            textAlign: 'center',
            mb: 3
          }}>
          Tra Cứu Phạt Nguội
        </Typography>
      )}

      <Box sx={{ maxWidth: 500, mx: 'auto', overflow: 'hidden' }}>
        {isSelectVehicleTypeUseIcon ? (
          <VehicleTypeIconSelector
            value={carType}
            options={vehicleTypeOptions}
            onChange={handleCarTypeChange}
            error={errors.carType}
          />
        ) : (
          <BasicSelect
            label="Chọn loại xe"
            value={carType}
            options={vehicleTypeOptions}
            onChange={handleCarTypeChange}
            error={errors.carType}
          />
        )}

        <BasicInput
          label="Biển số xe"
          placeholder="29A12345"
          value={plateNumber}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSubmit()
            }
          }}
          onChange={(event) => {
            setIsLookup(false)
            setIsAPIError(false)

            const value = normalizePlate(event.target.value)
            setPlateNumber(value)

            const plateError = validatePlateNumber(value)
            setErrors((prev) => ({ ...prev, plateNumber: plateError }))
          }}
          error={errors.plateNumber}
        />

        {showPhoneInput && (
          <BasicInput
            label="Số điện thoại"
            placeholder="VD: 0901234567"
            value={phoneNumber}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit()
              }
            }}
            onChange={(event) => {
              setIsLookup(false)
              setIsAPIError(false)

              const value = digitsOnly(event.target.value)
              setPhoneNumber(value)

              const phoneError = validatePhone(value, showPhoneInput)
              setErrors((prev) => ({ ...prev, phoneNumber: phoneError }))
            }}
            inputMode="numeric"
            maxLength={11}
            error={errors.phoneNumber}
          />
        )}

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: 'var(--v1-brand-primary)',
              '&:hover': {
                backgroundColor: 'var(--v1-brand-primary)'
              }
            }}>
            Tra Cứu
          </Button>

          {!isEmbeddedView && TRAFFIC_URLS.trafficFineWeb && (
            <Button
              variant="contained"
              onClick={handleOpenNotification}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: 'var(--v1-brand-primary)',
                '&:hover': {
                  backgroundColor: 'var(--v1-brand-primary)'
                }
              }}>
              Nhận thông báo phạt nguội
            </Button>
          )}
        </Box>

        {isLookup && (
          <>
            {!isLoading && !isEmbeddedView && linkSearch && (
              <Typography sx={{ mt: 2, textAlign: 'center' }}>
                <Link href={linkSearch} target="_blank" rel="noreferrer" sx={{ color: 'var(--v1-brand-primary)' }}>
                  Nguồn dữ liệu tham khảo tại {linkSearch}
                </Link>
              </Typography>
            )}

            <Box sx={{ mt: 4 }}>
              {isLoading ? (
                <LoadingSpinner text="Đang tải..." />
              ) : (
                <Box className="TrafficViolationsViolationLookup_result" sx={{ backgroundColor: resultBackground , color:isAPIError ? "var(--v1-text-primary)" : "var(--v1-text-inverse)"}}>
                  {isVehicleWarning ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Typography textAlign="center" variant="body1" fontWeight="bold" >
                        {criminalNotification || 'Phương tiện của bạn đang có cảnh báo!'}
                      </Typography>
                      <Typography
                        textAlign="center"
                        variant="body2"
                        onClick={handleGoToDetail}
                        className="TrafficViolationsViolationLookup_linkDetail">
                        Xem chi tiết tại đây
                      </Typography>
                    </Box>
                  ) : isAPIError ? (
                    <Typography textAlign="center" variant="body1" fontWeight="bold" sx={{ color: '#000' }}>
                      {criminalNotification || TRAFFIC_ERROR_MESSAGES.DEFAULT.message}
                    </Typography>
                  ) : (
                    <Typography textAlign="center" variant="body1" fontWeight="bold">
                      Phương tiện của bạn đang không có cảnh báo!
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Paper>
  )
}

